import {
  normalizeCandleTimeframe,
  resolveFinestCandleTimeframe,
  type CandleTimeframeId,
} from "./candleTimeframeBarBudget";
import {
  isPolicyTreeBundleV1,
  isPolicyTreeV2,
  type PolicyTreeBundleSegmentV1,
  type TradePolicySimulateRequest,
} from "../types/tradePolicy";

export type DualCandleTimeframeHints = {
  /**
   * UI / Run candle TF (often finest entry). Used as `candle_timeframe` when dual
   * or when locked / non-1m.
   */
  preferredCandleTimeframe?: string | null;
  /** When true, always send `candle_timeframe` (including 1m). */
  candleTimeframeLocked?: boolean;
  entryCandleTimeframe?: string | null;
  managementCandleTimeframe?: string | null;
};

export type DualCandleTimeframeResolution = {
  candle_timeframe: CandleTimeframeId;
  entry_candle_timeframe?: CandleTimeframeId;
  management_candle_timeframe?: CandleTimeframeId;
  /** Hybrid RA+EP (or entry+management bundle) with a resolved management TF. */
  isDual: boolean;
};

export type SimulateCandleTimeframeBodyFields = Pick<
  TradePolicySimulateRequest,
  "candle_timeframe" | "entry_candle_timeframe" | "management_candle_timeframe"
>;

function readTrimmedString(value: unknown): string {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function asMetaRecord(policy: Record<string, unknown>): Record<string, unknown> | null {
  const meta = policy.meta;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return null;
  return meta as Record<string, unknown>;
}

/** Top-level / meta candle TF on a policy tree (EP-only or stamped bundles). */
export function candleTimeframeFromPolicyTree(policy: unknown): string | null {
  if (!policy || typeof policy !== "object" || Array.isArray(policy)) return null;
  const record = policy as Record<string, unknown>;
  const top = readTrimmedString(record.candle_timeframe);
  if (top) return top;
  const meta = asMetaRecord(record);
  if (!meta) return null;
  return (
    readTrimmedString(meta.management_candle_timeframe) ||
    readTrimmedString(meta.candle_timeframe) ||
    null
  );
}

function segmentKind(segment: Record<string, unknown>): string {
  return readTrimmedString(segment.segment_kind).toLowerCase();
}

function segmentLooksLikeManagement(segment: Record<string, unknown>): boolean {
  const kind = segmentKind(segment);
  if (kind === "management") return true;
  if (kind === "entry_filter") return false;
  if (Array.isArray(segment.rules) && segment.rules.length > 0) return true;
  return false;
}

function segmentLooksLikeEntry(segment: Record<string, unknown>): boolean {
  const kind = segmentKind(segment);
  if (kind === "entry_filter") return true;
  if (kind === "management") return false;
  if (segmentLooksLikeManagement(segment)) return false;
  const action = segment.action;
  return action === "veto" || action === "keep_only" || action == null;
}

/** Collect stamped segment TFs, optionally filtered by kind. */
export function collectSegmentCandleTimeframes(
  policy: unknown,
  kinds?: Array<"entry_filter" | "management">
): string[] {
  if (!policy || typeof policy !== "object" || Array.isArray(policy)) return [];
  const record = policy as Record<string, unknown>;
  const segments = Array.isArray(record.segments) ? record.segments : [];
  const wantEntry = !kinds || kinds.includes("entry_filter");
  const wantMgmt = !kinds || kinds.includes("management");
  const out: string[] = [];
  for (const raw of segments) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const segment = raw as Record<string, unknown>;
    const isEntry = segmentLooksLikeEntry(segment);
    const isMgmt = segmentLooksLikeManagement(segment);
    if (kinds) {
      if (isEntry && !wantEntry) continue;
      if (isMgmt && !wantMgmt) continue;
      if (!isEntry && !isMgmt) continue;
    }
    const tf = readTrimmedString(segment.candle_timeframe);
    if (tf) out.push(tf);
  }
  return out;
}

export function policyHasEntryFilterSegments(policy: unknown): boolean {
  if (!policy || typeof policy !== "object" || Array.isArray(policy)) return false;
  if (isPolicyTreeV2(policy)) return false;
  const record = policy as Record<string, unknown>;
  if (!isPolicyTreeBundleV1(policy)) {
    // Classic v1 entry payload.
    return "action" in record && "root" in record;
  }
  return policy.segments.some((seg) =>
    segmentLooksLikeEntry(seg as unknown as Record<string, unknown>)
  );
}

export function policyHasManagementSide(policy: unknown): boolean {
  if (!policy || typeof policy !== "object" || Array.isArray(policy)) return false;
  if (isPolicyTreeV2(policy)) return true;
  if (!isPolicyTreeBundleV1(policy)) return false;
  return policy.segments.some((seg) =>
    segmentLooksLikeManagement(seg as unknown as Record<string, unknown>)
  );
}

/**
 * Resolve dual-TF fields for simulate.
 * Management TF follows BE inference order (hints / meta → management stamps → EP-only top-level),
 * never the finest entry lock alone.
 */
export function resolveDualCandleTimeframes(
  policy: unknown,
  hints: DualCandleTimeframeHints = {}
): DualCandleTimeframeResolution {
  const record =
    policy && typeof policy === "object" && !Array.isArray(policy)
      ? (policy as Record<string, unknown>)
      : null;
  const meta = record ? asMetaRecord(record) : null;

  const entryFromSegs = collectSegmentCandleTimeframes(policy, ["entry_filter"]);
  const mgmtFromSegs = collectSegmentCandleTimeframes(policy, ["management"]);

  const entryHint =
    readTrimmedString(hints.entryCandleTimeframe) ||
    readTrimmedString(meta?.entry_candle_timeframe) ||
    readTrimmedString(record?.entry_candle_timeframe);

  const managementHint =
    readTrimmedString(hints.managementCandleTimeframe) ||
    readTrimmedString(meta?.management_candle_timeframe) ||
    readTrimmedString(record?.management_candle_timeframe);

  const hasEntry = policyHasEntryFilterSegments(policy) || entryFromSegs.length > 0 || Boolean(entryHint);
  const hasManagement = policyHasManagementSide(policy) || mgmtFromSegs.length > 0 || Boolean(managementHint);

  const entry =
    (entryFromSegs.length > 0
      ? resolveFinestCandleTimeframe(entryFromSegs)
      : null) ||
    (entryHint ? normalizeCandleTimeframe(entryHint) : null);

  let management: CandleTimeframeId | null =
    mgmtFromSegs.length > 0 ? normalizeCandleTimeframe(mgmtFromSegs[0]!) : null;
  // Hints / meta only when the tree has no management stamp (avoid poisoning with a bad lock).
  if (!management && managementHint) {
    management = normalizeCandleTimeframe(managementHint);
  }
  if (!management && hasManagement && !hasEntry) {
    const top = candleTimeframeFromPolicyTree(policy);
    if (top) management = normalizeCandleTimeframe(top);
  }

  const locked = readTrimmedString(hints.preferredCandleTimeframe)
    ? normalizeCandleTimeframe(hints.preferredCandleTimeframe)
    : null;

  const candle_timeframe =
    locked ||
    (entry && management
      ? resolveFinestCandleTimeframe([entry, management])
      : null) ||
    entry ||
    management ||
    normalizeCandleTimeframe(candleTimeframeFromPolicyTree(policy));

  const isDual = Boolean(hasEntry && hasManagement && management);

  if (isDual && management) {
    return {
      candle_timeframe,
      entry_candle_timeframe: entry ?? candle_timeframe,
      management_candle_timeframe: management,
      isDual: true,
    };
  }

  return { candle_timeframe, isDual: false };
}

/**
 * Stamp management segments (and bundle meta) with the management TF.
 * Does not overwrite existing segment stamps.
 */
export function stampPolicyManagementCandleTimeframe(
  policy: unknown,
  managementTf: string
): unknown {
  const tf = normalizeCandleTimeframe(managementTf);
  if (isPolicyTreeV2(policy)) {
    const record = policy as unknown as Record<string, unknown>;
    if (readTrimmedString(record.candle_timeframe)) return policy;
    return { ...policy, candle_timeframe: tf };
  }
  if (!isPolicyTreeBundleV1(policy)) return policy;

  let stampedAny = false;
  const segments: PolicyTreeBundleSegmentV1[] = policy.segments.map((segment) => {
    const record = segment as unknown as Record<string, unknown>;
    if (!segmentLooksLikeManagement(record)) return segment;
    if (readTrimmedString(record.candle_timeframe)) return segment;
    stampedAny = true;
    return { ...segment, candle_timeframe: tf };
  });

  const prevMeta =
    policy.meta && typeof policy.meta === "object" ? { ...policy.meta } : {};
  const nextMeta = {
    ...prevMeta,
    management_candle_timeframe: tf,
  };

  if (!stampedAny && readTrimmedString(prevMeta.management_candle_timeframe) === tf) {
    return policy;
  }

  return {
    ...policy,
    segments,
    meta: nextMeta,
  };
}

/**
 * Stamp management TF onto the policy (when known) and build simulate body TF fields.
 * Dual RA+EP always sends candle + entry + management. Otherwise mirrors prior lock rules.
 */
export function prepareSimulatePolicyWithDualTimeframes(
  policy: unknown,
  hints: DualCandleTimeframeHints = {}
): { policy: unknown; fields: SimulateCandleTimeframeBodyFields } {
  const resolved = resolveDualCandleTimeframes(policy, hints);
  let nextPolicy = policy;
  if (resolved.management_candle_timeframe) {
    nextPolicy = stampPolicyManagementCandleTimeframe(
      policy,
      resolved.management_candle_timeframe
    );
  }

  const fields: SimulateCandleTimeframeBodyFields = {};
  const preferred = readTrimmedString(hints.preferredCandleTimeframe)
    ? normalizeCandleTimeframe(hints.preferredCandleTimeframe)
    : resolved.candle_timeframe;

  if (resolved.isDual && resolved.management_candle_timeframe) {
    fields.candle_timeframe = preferred;
    fields.entry_candle_timeframe =
      resolved.entry_candle_timeframe ?? preferred;
    fields.management_candle_timeframe = resolved.management_candle_timeframe;
    return { policy: nextPolicy, fields };
  }

  if (hints.candleTimeframeLocked || preferred !== "1m") {
    fields.candle_timeframe = preferred;
  }
  return { policy: nextPolicy, fields };
}
